// AccountingDashboard.jsx - Complete dashboard with monthly report, filters, and exports
import autoTable from "jspdf-autotable";
import React, { useState, useEffect } from "react";
import { apiClient } from "../apiClient";
import {
  groszyToPln,
  formatDate,
  getCategoryName,
  fetchCategories,
  getTodayISO,
  getFirstDayOfMonth,
  getMonthName,
  exportToCSV,
  exportToExcel,
  exportToPDF,
  removeDeliveryLink
} from "./accountingUtils";

export default function AccountingDashboard({ refreshTrigger }) {
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null); // 👈 ADDED: Backend summary data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [dateFrom, setDateFrom] = useState(getFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getTodayISO());
  const [typeFilter, setTypeFilter] = useState("all"); // all | income | expense
  const [searchTerm, setSearchTerm] = useState("");

  // Monthly Report
  const [showMonthlyReport, setShowMonthlyReport] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(getTodayISO().slice(0, 7)); // "2026-01"

  // Edit mode
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // ⚠️ TODO: Remove this line when accountingUtils.js is fixed to use apiClient
  const token = localStorage.getItem("authToken");

  // ==========================================
  // FETCH DATA
  // ==========================================
  const fetchData = async () => {
    if (!token) {
      setError("Not logged in");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch categories, income, expenses, AND summary in parallel
      const [categoriesData, incomeData, expensesData, summaryData] = await Promise.all([
        fetchCategories(token),
        apiClient.get("/accounting/income"),
        apiClient.get("/accounting/expenses"),
        apiClient.get("/accounting/summary")
      ]);

      setCategories(categoriesData);
      setIncome(incomeData);
      setExpenses(expensesData);
      setSummary(summaryData); // 👈 ADDED
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [refreshTrigger]);

  // ==========================================
  // FILTER & MERGE DATA
  // ==========================================
  const getFilteredEntries = () => {
    let entries = [];

    // Add income entries
    if (typeFilter === "all" || typeFilter === "income") {
      entries = entries.concat(
        income.map(item => ({
          ...item,
          type: "income",
          categoryName: getCategoryName(item.category_id, categories),
          displayDate: formatDate(item.date)
        }))
      );
    }

    // Add expense entries
    if (typeFilter === "all" || typeFilter === "expense") {
      entries = entries.concat(
        expenses.map(item => ({
          ...item,
          type: "expense",
          categoryName: getCategoryName(item.category_id, categories),
          displayDate: formatDate(item.date)
        }))
      );
    }

    // Filter by date range
    entries = entries.filter(e => {
      if (e.date === "unknown") return true;
      return e.date >= dateFrom && e.date <= dateTo;
    });

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      entries = entries.filter(e =>
        e.categoryName.toLowerCase().includes(search) ||
        (e.notes && e.notes.toLowerCase().includes(search))
      );
    }

    // Sort by date (newest first)
    entries.sort((a, b) => {
      if (a.date === "unknown") return 1;
      if (b.date === "unknown") return -1;
      return b.date.localeCompare(a.date);
    });

    return entries;
  };

  // ==========================================
  // MONTHLY REPORT DATA
  // ==========================================
  const getMonthlyReportData = () => {
    const monthStart = `${selectedMonth}-01`;
    const monthEnd = `${selectedMonth}-31`;

    // Filter income by selected month
    const monthIncome = income.filter(i =>
      i.date >= monthStart && i.date <= monthEnd
    );

    // Filter expenses by selected month
    const monthExpenses = expenses.filter(e =>
      e.date >= monthStart && e.date <= monthEnd
    );

    // Group income by category
    const incomeByCategory = monthIncome.reduce((acc, item) => {
      const catName = getCategoryName(item.category_id, categories);
      if (!acc[catName]) acc[catName] = 0;
      acc[catName] += item.amount_groszy;
      return acc;
    }, {});

    // Group expenses by category
    const expensesByCategory = monthExpenses.reduce((acc, item) => {
      const catName = getCategoryName(item.category_id, categories);
      if (!acc[catName]) acc[catName] = 0;
      acc[catName] += item.amount_groszy;
      return acc;
    }, {});

    const totalIncome = monthIncome.reduce((sum, i) => sum + i.amount_groszy, 0);
    const totalExpenses = monthExpenses.reduce((sum, e) => sum + e.amount_groszy, 0);
    const netBalance = totalIncome - totalExpenses;

    return {
      incomeByCategory,
      expensesByCategory,
      totalIncome,
      totalExpenses,
      netBalance
    };
  };

  // ==========================================
  // CALCULATIONS (Manual entries only - for table filtering)
  // ==========================================
  const calculateSummary = () => {
    const filtered = getFilteredEntries();
    const totalIncome = filtered
      .filter(e => e.type === "income")
      .reduce((sum, e) => sum + e.amount_groszy, 0);
    const totalExpenses = filtered
      .filter(e => e.type === "expense")
      .reduce((sum, e) => sum + e.amount_groszy, 0);
    const netBalance = totalIncome - totalExpenses;

    return { totalIncome, totalExpenses, netBalance };
  };

  // ==========================================
  // DELETE ENTRY
  // ==========================================
  const handleDelete = async (id, type) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      const endpoint = type === "income"
        ? `/accounting/income/${id}`
        : `/accounting/expenses/${id}`;

      await apiClient.delete(endpoint);

      // Refresh data: Full reload for income (updates client ledger), fetchData for expenses
      if (type === "income") {
        window.location.reload();
      } else {
        fetchData();
      }
    } catch (err) {
      alert("Failed to delete entry");
    }
  };

  // ==========================================
  // EDIT ENTRY
  // ==========================================
  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({
      type: entry.type,
      category_id: entry.category_id,
      amount: groszyToPln(entry.amount_groszy),
      date: entry.date,
      notes: removeDeliveryLink(entry.notes || "")
    });
  };

  const saveEdit = async () => {
    try {
      const endpoint = editForm.type === "income"
        ? `/accounting/income/${editingId}`
        : `/accounting/expenses/${editingId}`;

      const body = {
        category_id: editForm.category_id,
        amount_groszy: Math.round(parseFloat(editForm.amount) * 100),
        date: editForm.date,
        notes: editForm.notes || null
      };

      await apiClient.put(endpoint, body);

      setEditingId(null);
      fetchData();
    } catch (err) {
      alert("Failed to update entry");
    }
  };

  // ==========================================
  // EXPORT FUNCTIONS
  // ==========================================
  const handleExportCSV = () => {
    const entries = getFilteredEntries();
    const csvData = entries.map(e => ({
      Date: e.displayDate,
      Type: e.type === "income" ? "Income" : "Expense",
      Category: e.categoryName,
      Amount_PLN: groszyToPln(e.amount_groszy),
      Notes: removeDeliveryLink(e.notes || "")
    }));

    exportToCSV(csvData, `accounting_${dateFrom}_to_${dateTo}.csv`);
  };

  const handleExportExcel = () => {
    const entries = getFilteredEntries();
    const excelData = entries.map(e => ({
      Date: e.displayDate,
      Type: e.type === "income" ? "Income" : "Expense",
      Category: e.categoryName,
      "Amount (PLN)": groszyToPln(e.amount_groszy),
      Notes: removeDeliveryLink(e.notes || "")
    }));

    exportToExcel(excelData, `accounting_${dateFrom}_to_${dateTo}`);
  };

  const handleExportPDF = () => {
    const entries = getFilteredEntries();
    const headers = ["Date", "Type", "Category", "Amount (PLN)", "Notes"];
    const rows = entries.map(e => [
      e.displayDate,
      e.type === "income" ? "Income" : "Expense",
      e.categoryName,
      groszyToPln(e.amount_groszy),
      removeDeliveryLink(e.notes || "").substring(0, 30)
    ]);

    exportToPDF(
      `Accounting Report (${dateFrom} to ${dateTo})`,
      headers,
      rows,
      `accounting_${dateFrom}_to_${dateTo}.pdf`
    );
  };

  const handleExportMonthlyReportPDF = () => {
    const report = getMonthlyReportData();
    const monthName = getMonthName(`${selectedMonth}-15`);

    const headers = ["Category", "Amount (PLN)"];
    const incomeRows = Object.entries(report.incomeByCategory).map(([cat, amt]) => [
      cat,
      groszyToPln(amt)
    ]);
    const expenseRows = Object.entries(report.expensesByCategory).map(([cat, amt]) => [
      cat,
      groszyToPln(amt)
    ]);

    const rows = [
      ["INCOME", ""],
      ...incomeRows,
      ["TOTAL INCOME", groszyToPln(report.totalIncome)],
      ["", ""],
      ["EXPENSES", ""],
      ...expenseRows,
      ["TOTAL EXPENSES", groszyToPln(report.totalExpenses)],
      ["", ""],
      ["NET BALANCE", groszyToPln(report.netBalance)]
    ];

    exportToPDF(
      `Monthly Report - ${monthName}`,
      headers,
      rows,
      `monthly_report_${selectedMonth}.pdf`
    );
  };

  const handleExportMonthlyReportExcel = () => {
    const report = getMonthlyReportData();
    const monthName = getMonthName(`${selectedMonth}-15`);

    // Prepare data for Excel
    const excelData = [];

    // Income section
    excelData.push({ Category: "INCOME", "Amount (PLN)": "" });
    Object.entries(report.incomeByCategory).forEach(([cat, amt]) => {
      excelData.push({ Category: cat, "Amount (PLN)": groszyToPln(amt) });
    });
    excelData.push({ Category: "TOTAL INCOME", "Amount (PLN)": groszyToPln(report.totalIncome) });
    excelData.push({ Category: "", "Amount (PLN)": "" });

    // Expenses section
    excelData.push({ Category: "EXPENSES", "Amount (PLN)": "" });
    Object.entries(report.expensesByCategory).forEach(([cat, amt]) => {
      excelData.push({ Category: cat, "Amount (PLN)": groszyToPln(amt) });
    });
    excelData.push({ Category: "TOTAL EXPENSES", "Amount (PLN)": groszyToPln(report.totalExpenses) });
    excelData.push({ Category: "", "Amount (PLN)": "" });

    // Net balance
    excelData.push({ Category: "NET BALANCE", "Amount (PLN)": groszyToPln(report.netBalance) });

    exportToExcel(excelData, `monthly_report_${monthName.replace(/\s+/g, '_')}`);
  };

  // ==========================================
  // MONTH SELECTOR OPTIONS
  // ==========================================
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = d.toISOString().slice(0, 7);
      months.push(value);
    }
    return months;
  };

  // ==========================================
  // RENDER
  // ==========================================
  if (loading) {
    return (
      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
        <p className="text-[#2D2F5E] italic">Loading accounting data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 p-4 rounded-xl border border-red-300">
        <p className="text-red-700">❌ {error}</p>
      </div>
    );
  }

  const manualSummary = calculateSummary();
  const monthlyReport = getMonthlyReportData();
  const filteredEntries = getFilteredEntries();

  return (
    <div className="space-y-6">
      {/* ==========================================
          COMPLETE INCOME SUMMARY SECTION (NEW)
      ========================================== */}
      {summary && (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
          <h3 className="font-bold text-[#2D2F5E] text-lg mb-4">
            💰 Complete Income Summary (All Sources)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 font-medium">Total Income (All)</p>
              <p className="text-2xl font-bold text-blue-800">
                {groszyToPln(summary.total_income)} PLN
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-xs text-green-700 font-medium">Manual Income</p>
              <p className="text-2xl font-bold text-green-800">
                {groszyToPln(summary.manual_income_total)} PLN
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-700 font-medium">Delivery Payments</p>
              <p className="text-2xl font-bold text-purple-800">
                {groszyToPln(summary.delivery_payments_total)} PLN
              </p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-xs text-red-700 font-medium">Total Expenses</p>
              <p className="text-2xl font-bold text-red-800">
                {groszyToPln(summary.total_expenses)} PLN
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          MONTHLY REPORT SECTION
      ========================================== */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
        <div
          onClick={() => setShowMonthlyReport(!showMonthlyReport)}
          className="flex justify-between items-center cursor-pointer"
        >
          <h3 className="font-bold text-[#2D2F5E] text-lg">
            📊 Monthly Report - {getMonthName(`${selectedMonth}-15`)}
          </h3>
          <button className="text-[#2D2F5E] font-bold text-xl">
            {showMonthlyReport ? "▼" : "▶"}
          </button>
        </div>

        {showMonthlyReport && (
          <div className="mt-4 space-y-4">
            {/* Month Selector & Export */}
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="flex-1 p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
              >
                {getMonthOptions().map(month => (
                  <option key={month} value={month}>
                    {getMonthName(`${month}-15`)}
                  </option>
                ))}
              </select>

              <button
                onClick={handleExportMonthlyReportPDF}
                className="bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-2 rounded transition text-sm font-medium"
              >
                📄 PDF
              </button>

              <button
                onClick={handleExportMonthlyReportExcel}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition text-sm font-medium"
              >
                📊 Excel
              </button>
            </div>

            {/* Two-Column Report */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-[#2D2F5E] border-opacity-20 rounded-lg overflow-hidden">
              {/* INCOME Column */}
              <div className="bg-[#F5E9D8] p-4">
                <h4 className="font-bold text-[#2D2F5E] mb-3 text-center border-b border-[#2D2F5E] border-opacity-30 pb-2">
                  💰 INCOME
                </h4>
                <div className="space-y-2">
                  {Object.keys(monthlyReport.incomeByCategory).length > 0 ? (
                    <>
                      {Object.entries(monthlyReport.incomeByCategory).map(([cat, amount]) => (
                        <div key={cat} className="flex justify-between text-sm">
                          <span className="text-[#2D2F5E]">{cat}</span>
                          <span className="font-medium text-green-700">
                            {groszyToPln(amount)} PLN
                          </span>
                        </div>
                      ))}
                      <div className="border-t border-[#2D2F5E] border-opacity-30 pt-2 mt-2 flex justify-between font-bold">
                        <span className="text-[#2D2F5E]">TOTAL</span>
                        <span className="text-green-700">
                          {groszyToPln(monthlyReport.totalIncome)} PLN
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-[#2D2F5E] italic text-center">No income this month</p>
                  )}
                </div>
              </div>

              {/* EXPENSES Column */}
              <div className="bg-[#F5E9D8] p-4">
                <h4 className="font-bold text-[#2D2F5E] mb-3 text-center border-b border-[#2D2F5E] border-opacity-30 pb-2">
                  💸 EXPENSES
                </h4>
                <div className="space-y-2">
                  {Object.keys(monthlyReport.expensesByCategory).length > 0 ? (
                    <>
                      {Object.entries(monthlyReport.expensesByCategory).map(([cat, amount]) => (
                        <div key={cat} className="flex justify-between text-sm">
                          <span className="text-[#2D2F5E]">{cat}</span>
                          <span className="font-medium text-red-700">
                            {groszyToPln(amount)} PLN
                          </span>
                        </div>
                      ))}
                      <div className="border-t border-[#2D2F5E] border-opacity-30 pt-2 mt-2 flex justify-between font-bold">
                        <span className="text-[#2D2F5E]">TOTAL</span>
                        <span className="text-red-700">
                          {groszyToPln(monthlyReport.totalExpenses)} PLN
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-[#2D2F5E] italic text-center">No expenses this month</p>
                  )}
                </div>
              </div>
            </div>

            {/* Net Balance */}
            <div className={`p-4 rounded-lg text-center font-bold text-lg ${monthlyReport.netBalance >= 0
                ? "bg-green-100 text-green-800 border border-green-300"
                : "bg-red-100 text-red-800 border border-red-300"
              }`}>
              NET BALANCE: {monthlyReport.netBalance >= 0 ? "+" : ""}
              {groszyToPln(monthlyReport.netBalance)} PLN
            </div>
          </div>
        )}
      </div>

      {/* ==========================================
          RECENT ENTRIES SECTION (Manual only - for editing)
      ========================================== */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
        <h3 className="font-bold text-[#2D2F5E] text-lg mb-4">📋 Recent Manual Entries</h3>

        {/* Summary Cards (Manual entries only) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <p className="text-xs text-green-700 font-medium">Manual Income</p>
            <p className="text-2xl font-bold text-green-800">
              {groszyToPln(manualSummary.totalIncome)} PLN
            </p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <p className="text-xs text-red-700 font-medium">Manual Expenses</p>
            <p className="text-2xl font-bold text-red-800">
              {groszyToPln(manualSummary.totalExpenses)} PLN
            </p>
          </div>
          <div className={`p-4 rounded-lg border ${manualSummary.netBalance >= 0
              ? "bg-blue-50 border-blue-200"
              : "bg-red-50 border-red-200"
            }`}>
            <p className={`text-xs font-medium ${manualSummary.netBalance >= 0 ? "text-blue-700" : "text-red-700"
              }`}>
              Net Balance (Manual)
            </p>
            <p className={`text-2xl font-bold ${manualSummary.netBalance >= 0 ? "text-blue-800" : "text-red-800"
              }`}>
              {manualSummary.netBalance >= 0 ? "+" : ""}
              {groszyToPln(manualSummary.netBalance)} PLN
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#F5E9D8] p-4 rounded-lg border border-[#2D2F5E] border-opacity-20 mb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#2D2F5E] mb-1">From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-sm text-[#2D2F5E] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2D2F5E] mb-1">To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-sm text-[#2D2F5E] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2D2F5E] mb-1">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-sm text-[#2D2F5E] bg-white"
              >
                <option value="all">All</option>
                <option value="income">Income Only</option>
                <option value="expense">Expenses Only</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2D2F5E] mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search category, notes..."
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-sm text-[#2D2F5E] bg-white"
              />
            </div>
          </div>

          {/* Export Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleExportPDF}
              className="bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-2 rounded transition text-sm font-medium"
            >
              📄 PDF
            </button>
            <button
              onClick={handleExportExcel}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition text-sm font-medium"
            >
              📊 Excel
            </button>
            <button
              onClick={handleExportCSV}
              className="bg-[#2D2F5E] hover:bg-[#1D1F4E] text-white px-4 py-2 rounded transition text-sm font-medium"
            >
              📋 CSV
            </button>
          </div>
        </div>

        {/* Entries Table (Desktop) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#2D2F5E] text-white">
                <th className="p-3 text-left text-sm">Date</th>
                <th className="p-3 text-left text-sm">Type</th>
                <th className="p-3 text-left text-sm">Category</th>
                <th className="p-3 text-right text-sm">Amount (PLN)</th>
                <th className="p-3 text-left text-sm">Notes</th>
                <th className="p-3 text-center text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-4 text-center text-[#2D2F5E] italic">
                    No entries found
                  </td>
                </tr>
              ) : (
                filteredEntries.map((entry) => (
                  <tr
                    key={`${entry.type}-${entry.id}`}
                    className="border-b border-[#2D2F5E] border-opacity-10 hover:bg-[#F5E9D8]"
                  >
                    {editingId === entry.id ? (
                      // Edit Mode
                      <>
                        <td className="p-3">
                          <input
                            type="date"
                            value={editForm.date}
                            onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                            className="w-full p-1 border rounded text-sm"
                          />
                        </td>
                        <td className="p-3 text-sm">{entry.type === "income" ? "💰" : "💸"}</td>
                        <td className="p-3 text-sm">{entry.categoryName}</td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={editForm.amount}
                            onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                            className="w-full p-1 border rounded text-sm text-right"
                            step="0.01"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="text"
                            value={editForm.notes}
                            onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                            className="w-full p-1 border rounded text-sm"
                          />
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={saveEdit}
                            className="bg-green-600 text-white px-2 py-1 rounded text-xs mr-1"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="bg-gray-600 text-white px-2 py-1 rounded text-xs"
                          >
                            ✕
                          </button>
                        </td>
                      </>
                    ) : (
                      // View Mode
                      <>
                        <td className="p-3 text-sm text-[#2D2F5E]">{entry.displayDate}</td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-1 rounded ${entry.type === "income"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                            }`}>
                            {entry.type === "income" ? "💰 Income" : "💸 Expense"}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-[#2D2F5E] font-medium">
                          {entry.categoryName}
                        </td>
                        <td className={`p-3 text-sm font-bold text-right ${entry.type === "income" ? "text-green-700" : "text-red-700"
                          }`}>
                          {groszyToPln(entry.amount_groszy)}
                        </td>
                        <td className="p-3 text-sm text-[#2D2F5E] italic">
                          {removeDeliveryLink(entry.notes || "-")}
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => startEdit(entry)}
                            className="bg-blue-600 text-white px-2 py-1 rounded text-xs mr-1"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(entry.id, entry.type)}
                            className="bg-red-600 text-white px-2 py-1 rounded text-xs"
                          >
                            🗑️
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Entries Cards (Mobile) */}
        <div className="md:hidden space-y-3">
          {filteredEntries.length === 0 ? (
            <p className="text-center text-[#2D2F5E] italic">No entries found</p>
          ) : (
            filteredEntries.map((entry) => (
              <div
                key={`${entry.type}-${entry.id}`}
                className="bg-[#F5E9D8] p-4 rounded-lg border border-[#2D2F5E] border-opacity-20"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className={`text-xs px-2 py-1 rounded font-medium ${entry.type === "income"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                    }`}>
                    {entry.type === "income" ? "💰 Income" : "💸 Expense"}
                  </span>
                  <span className="text-xs text-[#2D2F5E]">{entry.displayDate}</span>
                </div>
                <div className="font-bold text-[#2D2F5E] mb-1">{entry.categoryName}</div>
                <div className={`text-xl font-bold mb-2 ${entry.type === "income" ? "text-green-700" : "text-red-700"
                  }`}>
                  {groszyToPln(entry.amount_groszy)} PLN
                </div>
                {entry.notes && (
                  <div className="text-sm text-[#2D2F5E] italic mb-3">
                    {removeDeliveryLink(entry.notes)}
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(entry)}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id, entry.type)}
                    className="flex-1 bg-red-600 text-white px-3 py-2 rounded text-sm"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}